import React, {useContext, useMemo} from "react";
import {ShowProductConfig} from "ts/api/product";
import groupBy from "lodash.groupby";
import {isColorItem} from "tools/custom-product-option/common";
import {ICustomProductOptionConfigItem} from "@evennt-tech/api-v2-core";
import FlexBox from "view/components/Layout/FlexBox";
import {Typography, TypographyProps} from "@material-ui/core";
import {getCpoColorNames} from "view/components/ProductCard/utils/helpers";
import formatPrice from "tools/formats/formatPrice";
import {CurrencyContext} from "view/context/CurrencyContext";
import {useLocalizedNameTranslate} from "hooks/useLocalizedNameTranslate";
import {resolveLocalizedConfig} from "tools/localization/resolveLocalizedProduct";
import useStyles from "./styles";

const COLOR_KEY = "color" as const;

const ProductItemCpoConfigRow = ({config}: {config: ShowProductConfig}) => {
  const classes = useStyles();
  const currency = useContext(CurrencyContext);
  const t = useLocalizedNameTranslate();

  const configItemsByCpoId = useMemo<
    [string, ICustomProductOptionConfigItem[]][]
  >(() => {
    const {items} = resolveLocalizedConfig(config, t);
    const itemsByCpo = groupBy(items ?? [], (item) => {
      if (isColorItem(item)) {
        return COLOR_KEY;
      }

      return `${item.cpoId}_${item.type}`;
    });

    return Object.entries(itemsByCpo) as [
      string,
      ICustomProductOptionConfigItem[]
    ][];
  }, [config, t]);

  if (!configItemsByCpoId.length) {
    return null;
  }

  const priceUplift = config.priceUplift ? (
    <Typography variant="h5" className={classes.label}>
      (+{formatPrice(config.priceUplift, currency)})
    </Typography>
  ) : null;

  return (
    <FlexBox alignCenter gap={2}>
      {configItemsByCpoId.map(([key, configItems]) => {
        if (!configItems.length) {
          return null;
        }

        const title = configItems[0]?.cpo?.title ?? "";

        return (
          <Item
            configItems={configItems}
            title={
              key === COLOR_KEY && t ? t("common.shopping_page.colour") : title
            }
            key={key}
          />
        );
      })}
      {priceUplift}
    </FlexBox>
  );
};

const Item = ({
  configItems,
  title,
}: {
  configItems: ICustomProductOptionConfigItem[];
  title: string;
}) => {
  const classes = useStyles();

  const [firstColorName, secondColorName] = getCpoColorNames({
    items: configItems,
  });

  const textProps: TypographyProps = {
    variant: "h5",
    className: classes.label,
    display: "inline",
  };

  return (
    <span>
      <Typography {...textProps} color="primary">
        {title}:{" "}
      </Typography>
      <Typography {...textProps} variant="h5">
        {`${firstColorName}${secondColorName ? `-${secondColorName}` : ""}`}
      </Typography>{" "}
    </span>
  );
};
export default ProductItemCpoConfigRow;
